import React, { useState } from "react";
import { Modal, Button, Typography, message, Divider, Space, Tag, Alert } from "antd";
import { CopyOutlined } from "@ant-design/icons";

const { Text } = Typography;

export interface SentLinkDto {
  id?: string;
  email: string;
  first_name: string;
  last_name: string;
  role: "Client" | "Cosigner";
  sent_link?: string;
}

interface CopyPDLinksProps {
  open: boolean;
  onClose: () => void;
  links: SentLinkDto[];
}

const copyToClipboard = async (text: string, label: string) => {
  try {
    await navigator.clipboard.writeText(text);
    message.success(`Copied link for ${label}`);
  } catch {
    message.error("Failed to copy link");
  }
};

const LinkRow: React.FC<{ item: SentLinkDto }> = ({ item }) => {
  const fullName = `${item.first_name} ${item.last_name}`.trim();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 12px",
        border: "1px solid #f0f0f0",
        borderRadius: 6,
        marginBottom: 8,
      }}
    >
      <div style={{ overflow: "hidden" }}>
        <Text strong>{fullName || "Unnamed"}</Text>
        <br />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {item.email}
        </Text>
      </div>
      <Button
        icon={<CopyOutlined />}
        size="small"
        disabled={!item.sent_link}
        onClick={() => item.sent_link && copyToClipboard(item.sent_link, fullName || item.email)}
      >
        Copy
      </Button>
    </div>
  );
};

const CopyPDLinks: React.FC<CopyPDLinksProps> = ({ open, onClose, links }) => {
  const clients = links.filter((l) => l.role === "Client");
  const cosigners = links.filter((l) => l.role === "Cosigner");

  return (
    <Modal
      title="Copy Links"
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      width={520}
    >
      <Alert
        showIcon
        className="mb-4"
        message="As this is a PandaDoc hosted link, it won't automatically modify the status of the application. Please advise the client to return back to the client portal once they are done signing. The signature page in client portal will acknowledge the changes made in the hosted link."
      />
      <Space direction="vertical" style={{ width: "100%" }}>
        <div>
          <Tag color="blue">Clients</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {clients.length} link{clients.length !== 1 ? "s" : ""}
          </Text>
        </div>
        {clients.length ? (
          clients.map((item, idx) => <LinkRow key={`client-${idx}`} item={item} />)
        ) : (
          <Text type="secondary">No client links.</Text>
        )}

        <Divider style={{ margin: "12px 0" }} />

        <div>
          <Tag color="purple">Cosigners</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {cosigners.length} link{cosigners.length !== 1 ? "s" : ""}
          </Text>
        </div>
        {cosigners.length ? (
          cosigners.map((item, idx) => <LinkRow key={`cosigner-${idx}`} item={item} />)
        ) : (
          <Text type="secondary">No cosigner links.</Text>
        )}
      </Space>
    </Modal>
  );
};

export default CopyPDLinks;
